import React, { useRef, useState } from "react";
import { FileSpreadsheet, X } from "lucide-react";
import Icon from "../icon/Icon";
import Button from "../button/Button";
import uploadImage from "../../assets/images/upload-image.png";

type FileUploadProps = {
    onFileSelect: (file: File) => void;
    acceptedFileTypes?: string;
    maxFileSize?: number; // in MB
    className?: string;
};

const FileUpload = ({
    onFileSelect,
    acceptedFileTypes = ".csv",
    maxFileSize = 100, // Increased to 100MB
    className = "",
}: FileUploadProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string>("");
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): boolean => {
        setError("");

        // Check file type
        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
        const acceptedTypes = acceptedFileTypes.split(",").map((type) => type.trim());
        
        if (!acceptedTypes.includes(fileExtension)) {
            setError(`Please upload a valid file type (${acceptedFileTypes})`);
            return false;
        }

        // Check file size
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > maxFileSize) {
            setError(`File size must be less than ${maxFileSize}MB`);
            return false;
        }

        return true;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && validateFile(file)) {
            setSelectedFile(file);
            onFileSelect(file);
        }
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const file = event.dataTransfer.files?.[0];
        if (file && validateFile(file)) {
            setSelectedFile(file);
            onFileSelect(file);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleBrowseClick();
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
    };

    return (
        <div className={`w-full ${className}`}>
            {!selectedFile ? (
                <div
                    className={`
                        relative border-2 border-dashed rounded-lg p-24 text-center 
                        transition-all duration-200 cursor-pointer min-h-[600px] flex items-center justify-center
                        bg-white
                    `}
                    style={{
                        borderColor: isDragging ? "#7C3AED" : "#DAB0FB"
                    }}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleBrowseClick}
                    onKeyDown={handleKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label="Upload file area. Click or drag and drop a file to upload"
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptedFileTypes}
                        onChange={handleFileChange}
                        className="hidden"
                        aria-hidden="true"
                    />

                    <div className="flex flex-col items-center space-y-6">
                        {/* Upload Icon */}
                        <div className="flex items-center justify-center">
                            <img
                                src={uploadImage}
                                alt="Upload"
                                className="w-24"
                            />
                        </div>

                        {/* Text */}
                        <div className="space-y-3">
                            <p className="text-charcoal font-semibold text-lg">
                                Drag and drop your file here
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="flex-1 h-px bg-border"></div>
                                <p className="text-muted">
                                    or
                                </p>
                                <div className="flex-1 h-px bg-border"></div>
                            </div>
                        </div>

                        {/* Browse Button */}
                        <Button
                            variant="primary"
                            size="medium"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleBrowseClick();
                            }}
                        >
                            Browse Files
                        </Button>

                        {/* File Info */}
                        <p className="text-muted text-sm">
                            Supported formats: {acceptedFileTypes} • Max size: {maxFileSize}MB
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center gap-4">
                        {/* File Icon */}
                        <div
                            className="flex-shrink-0 p-3 rounded-md"
                            style={{ backgroundColor: "#26A69A" }}
                        >
                            <Icon
                                icon={FileSpreadsheet}
                                size={32}
                                className="text-white"
                            />
                        </div>

                        {/* File Info */}
                        <div className="flex-1 min-w-0">
                            <h4 className="text-charcoal font-semibold truncate">
                                {selectedFile.name}
                            </h4>
                            <p className="text-muted text-sm">
                                {formatFileSize(selectedFile.size)}
                            </p>
                        </div>

                        {/* Remove Button */}
                        <button
                            onClick={handleRemoveFile}
                            className="
                                flex-shrink-0 p-2 rounded-md 
                                bg-cloud hover:bg-cloud-shade 
                                transition-colors duration-150
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                            "
                            aria-label="Remove file"
                            type="button"
                        >
                            <Icon
                                icon={X}
                                size={20}
                                className="text-charcoal"
                            />
                        </button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div
                    className="mt-4 p-4 rounded-md bg-warning-tint text-white text-base font-medium"
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </div>
            )}
        </div>
    );
};

export default FileUpload;
